import { useEffect, useCallback, useRef } from 'react';

interface KeyPressCounterOptions {
  targetKey: string;
  requiredCount: number;
  resetTimeoutMs?: number;
}

export const useKeyPressCounter = (
  options: KeyPressCounterOptions,
  onTargetReached: () => void
) => {
  const { targetKey, requiredCount, resetTimeoutMs = 3000 } = options;
  const pressCountRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetCounter = useCallback(() => {
    pressCountRef.current = 0;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.code === targetKey) {
      pressCountRef.current += 1;

      // Reset timeout on each press
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (pressCountRef.current >= requiredCount) {
        onTargetReached();
        resetCounter();
      } else {
        // Set timeout to reset counter if no more presses
        timeoutRef.current = setTimeout(resetCounter, resetTimeoutMs);
      }
    }
  }, [targetKey, requiredCount, resetTimeoutMs, onTargetReached, resetCounter]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleKeyPress]);

  return {
    currentCount: pressCountRef.current,
    resetCounter
  };
};